/**
 * /audit-log — Audit log viewer (Story 7.11, admin-only).
 *
 * Surface:
 *   - Filters: userId (uuid), action substring, resourceType, date range
 *   - Paginated list → row click opens the detail dialog with payload diff
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, apiClient } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';
import { AuditDetailDialog, type AuditDetailDto } from '@/components/audit/AuditDetailDialog';
import { useAuthStore } from '@/stores/auth';

interface AuditRow {
  id: string;
  adminUserId: string | null;
  actorEmail: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  correlationId: string | null;
  createdAt: string;
}

interface ListResponse {
  data: AuditRow[];
  page: number;
  pageSize: number;
  total: number;
}

interface Filters {
  userId: string;
  action: string;
  resourceType: string;
  dateFrom: string;
  dateTo: string;
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string } };
    if (body?.error?.code === 'AUDIT_LOG_NOT_FOUND') return 'Linha não encontrada.';
    if (body?.error?.code === 'INSUFFICIENT_ROLE') return 'Apenas administradores.';
    if (body?.error?.message) return body.error.message;
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function AuditLogPage() {
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser?.role === 'admin';

  const [filters, setFilters] = useState<Filters>({
    userId: '',
    action: '',
    resourceType: '',
    dateFrom: '',
    dateTo: '',
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['audit-log', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('pageSize', '50');
      if (filters.userId) params.set('userId', filters.userId);
      if (filters.action) params.set('action', filters.action);
      if (filters.resourceType) params.set('resourceType', filters.resourceType);
      if (filters.dateFrom) params.set('dateFrom', new Date(filters.dateFrom).toISOString());
      if (filters.dateTo) params.set('dateTo', new Date(filters.dateTo).toISOString());
      return apiClient.get<ListResponse>(`/admin/audit-log?${params.toString()}`);
    },
    enabled: isAdmin,
  });

  const detailQuery = useQuery({
    queryKey: ['audit-log', 'detail', selectedId],
    queryFn: () => apiClient.get<AuditDetailDto>(`/admin/audit-log/${selectedId}`),
    enabled: isAdmin && selectedId !== null,
  });

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Audit Log</h1>
        </div>
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Apenas administradores têm acesso a essa área.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Audit Log</h1>
        <p className="text-sm text-muted-foreground">
          Trilha de auditoria completa das ações administrativas (append-only).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-5">
            <div className="space-y-1">
              <Label htmlFor="filter-userId">User ID</Label>
              <Input
                id="filter-userId"
                aria-label="User ID"
                placeholder="uuid"
                value={filters.userId}
                onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="filter-action">Action</Label>
              <Input
                id="filter-action"
                aria-label="Action"
                placeholder="user."
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="filter-resourceType">Resource</Label>
              <Input
                id="filter-resourceType"
                aria-label="Resource type"
                placeholder="admin_user"
                value={filters.resourceType}
                onChange={(e) => setFilters({ ...filters, resourceType: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="filter-dateFrom">De</Label>
              <Input
                id="filter-dateFrom"
                aria-label="Date from"
                type="datetime-local"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="filter-dateTo">Até</Label>
              <Input
                id="filter-dateTo"
                aria-label="Date to"
                type="datetime-local"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button
              variant="ghost"
              onClick={() =>
                setFilters({ userId: '', action: '', resourceType: '', dateFrom: '', dateTo: '' })
              }
            >
              Limpar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
          ) : listQuery.error ? (
            <div className="p-6 text-sm text-destructive">
              {humanizeError(listQuery.error, 'Falha ao carregar audit log.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Timestamp</th>
                    <th className="px-4 py-2">Actor</th>
                    <th className="px-4 py-2">Action</th>
                    <th className="px-4 py-2">Resource</th>
                    <th className="px-4 py-2">Resource id</th>
                    <th className="px-4 py-2">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {listQuery.data && listQuery.data.data.length > 0 ? (
                    listQuery.data.data.map((row) => (
                      <tr
                        key={row.id}
                        className="cursor-pointer border-b last:border-b-0 hover:bg-accent/40"
                        onClick={() => setSelectedId(row.id)}
                      >
                        <td className="px-4 py-2 text-muted-foreground">
                          {formatDateTime(row.createdAt)}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">
                          {row.actorEmail ?? row.adminUserId ?? '—'}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{row.action}</td>
                        <td className="px-4 py-2 font-mono text-xs">{row.resourceType}</td>
                        <td className="px-4 py-2 font-mono text-xs">{row.resourceId ?? '—'}</td>
                        <td className="px-4 py-2 font-mono text-xs">{row.ipAddress ?? '—'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhum evento no intervalo selecionado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AuditDetailDialog
        open={selectedId !== null}
        entry={detailQuery.data ?? null}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

export default AuditLogPage;
